import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTeam } from '../contexts/TeamContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, CheckSquare, Clock, AlertCircle, CheckCircle2, User, Calendar, MapPin } from 'lucide-react';

const API_URL = window.location.origin || '';

const Tasks = () => {
  const { currentTeam } = useTeam();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    if (currentTeam?.id) {
      fetchTasks();
      fetchTeamMembers();
    }
  }, [currentTeam?.id]); // Only re-run when team ID changes

  useEffect(() => {
    filterTasks();
  }, [tasks, selectedStatus]);

  const fetchTasks = async () => {
    // Only show loading spinner on initial load (when no tasks exist)
    if (tasks.length === 0) {
      setLoading(true);
    }
    try {
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/tasks`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      } else {
        toast.error('Failed to load tasks');
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Error loading tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/members`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Transform the nested structure to flat for easier use
        const flatMembers = data.map(item => ({
          user_id: item.user.id,
          user_name: item.user.name,
          user_email: item.user.email,
          role: item.membership.role
        }));
        setTeamMembers(flatMembers);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const filterTasks = () => {
    if (selectedStatus === 'all') {
      setFilteredTasks(tasks);
    } else {
      setFilteredTasks(tasks.filter(task => task.status === selectedStatus));
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Task status updated');
        fetchTasks();
      } else {
        toast.error('Failed to update task');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Error updating task');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="w-4 h-4" />,
      in_progress: <AlertCircle className="w-4 h-4" />,
      needs_help: <AlertCircle className="w-4 h-4 text-orange-600" />,
      completed: <CheckCircle2 className="w-4 h-4 text-green-600" />
    };
    return icons[status] || icons.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && dueDate.status !== 'completed';
  };

  if (!currentTeam) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Please select a team to view tasks</p>
        </div>
      </Layout>
    );
  }

  // Only show full-page loading on initial load when there's no data
  if (loading && tasks.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const statusCounts = {
    all: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    needs_help: tasks.filter(t => t.status === 'needs_help').length,
    completed: tasks.filter(t => t.status === 'completed').length
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
            <h1 className="text-3xl font-bold">Tasks</h1>
            <p className="text-gray-600 mt-1">Manage and track team tasks</p>
          </div>
          <CreateTaskDialog onSuccess={fetchTasks} teamMembers={teamMembers} />
        </div>

        {/* Status Tabs */}
        <Tabs value={selectedStatus} onValueChange={setSelectedStatus} className="mb-6">
          <TabsList className="glass-effect">
            <TabsTrigger value="all">
              All ({statusCounts.all})
            </TabsTrigger>
            <TabsTrigger value="pending">
              <Clock className="w-4 h-4 mr-2" />
              Pending ({statusCounts.pending})
            </TabsTrigger>
            <TabsTrigger value="in_progress">
              <AlertCircle className="w-4 h-4 mr-2" />
              In Progress ({statusCounts.in_progress})
            </TabsTrigger>
            <TabsTrigger value="needs_help">
              <AlertCircle className="w-4 h-4 mr-2 text-orange-600" />
              Needs Help ({statusCounts.needs_help})
            </TabsTrigger>
            <TabsTrigger value="completed">
              <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
              Completed ({statusCounts.completed})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Tasks Grid */}
        {filteredTasks.length === 0 ? (
          <div className="text-center py-12 dashboard-card">
            <CheckSquare className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No tasks found</h3>
            <p className="text-gray-600 mb-4">
              {tasks.length === 0
                ? 'Create your first task to get started'
                : 'No tasks match this status'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                teamMembers={teamMembers}
                onStatusChange={handleStatusChange}
                onUpdate={fetchTasks}
              />
            ))}
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

const TaskCard = ({ task, teamMembers, onStatusChange, onUpdate }) => {
  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.medium;
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'completed') return false;
    return new Date(dueDate) < new Date();
  };

  const assignedMember = teamMembers.find(m => m.user_id === task.assigned_to);

  return (
    <Card className="hover:shadow-lg transition-shadow" style={{ backgroundColor: 'white', borderColor: 'rgb(203, 213, 225)' }}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-start gap-3 mb-2">
              <div className="mt-1">
                {task.status === 'completed' ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : task.status === 'needs_help' ? (
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                ) : task.status === 'in_progress' ? (
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                ) : (
                  <Clock className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold">{task.title}</h3>
                {task.description && (
                  <p className="text-gray-600 mt-1">{task.description}</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              <Badge className={getPriorityColor(task.priority)}>
                {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
              </Badge>
              
              <Badge variant="outline">{task.dashboard_area}</Badge>

              {task.due_date && (
                <Badge variant="outline" className={isOverdue(task.due_date, task.status) ? 'bg-red-50 text-red-700 border-red-200' : ''}>
                  <Calendar className="w-3 h-3 mr-1" />
                  {isOverdue(task.due_date, task.status) ? 'Overdue: ' : ''}{formatDate(task.due_date)}
                </Badge>
              )}

              {assignedMember && (
                <Badge variant="outline">
                  <User className="w-3 h-3 mr-1" />
                  {assignedMember.user_name}
                </Badge>
              )}

              {task.location_required && (
                <Badge variant="outline">
                  <MapPin className="w-3 h-3 mr-1" />
                  {task.location_details || 'Location required'}
                </Badge>
              )}
            </div>
          </div>

          <div className="ml-4">
            <select
              value={task.status}
              onChange={(e) => onStatusChange(task.id, e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-opacity-20"
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="needs_help">Needs Help</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CreateTaskDialog = ({ onSuccess, teamMembers }) => {
  const { currentTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    assigned_to: '',
    project_id: '', // NEW: Link to project
    location_required: false,
    location_details: '',
    dashboard_area: 'medical',
    shared_with_support: false,
    shared_with_providers: false
  });

  // Fetch projects when dialog opens
  useEffect(() => {
    if (open && currentTeam) {
      fetchProjects();
    }
  }, [open, currentTeam]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/projects`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        assigned_to: formData.assigned_to || null,
        project_id: formData.project_id || null // Include project_id
      };

      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Task created successfully');
        setOpen(false);
        onSuccess();
        setFormData({
          title: '',
          description: '',
          due_date: '',
          priority: 'medium',
          assigned_to: '',
          project_id: '', // Reset project_id
          location_required: false,
          location_details: '',
          dashboard_area: 'medical',
          shared_with_support: false,
          shared_with_providers: false
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create task');
      }
    } catch (error) {
      console.error('Create task error:', error);
      toast.error('Error creating task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-700 hover:bg-blue-800">
          <Plus className="w-4 h-4 mr-2" />
          Create Task
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
          <DialogDescription>
            Add a new task for your care team
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Task Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Pick up prescription"
              required
              className="mt-1 border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-opacity-20"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add details about this task..."
              className="mt-1 border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-opacity-20"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="priority">Priority *</Label>
              <select
                id="priority"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1 focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-opacity-20"
                required
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <Label htmlFor="due_date">Due Date</Label>
              <Input
                id="due_date"
                type="datetime-local"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="mt-1 border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-opacity-20"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="assigned_to">Assign To</Label>
              <select
                id="assigned_to"
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1 focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-opacity-20"
              >
                <option value="">Unassigned</option>
                {teamMembers.map(member => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.user_name} ({member.role})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="project_id">Link to Project (Optional)</Label>
              <select
                id="project_id"
                value={formData.project_id}
                onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1 focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-opacity-20"
              >
                <option value="">No Project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="dashboard_area">Category *</Label>
            <select
              id="dashboard_area"
              value={formData.dashboard_area}
              onChange={(e) => setFormData({ ...formData, dashboard_area: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1 focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-opacity-20"
              required
            >
                <option value="medical">Medical</option>
                <option value="financial">Financial</option>
                <option value="legal">Legal</option>
                <option value="household">Household</option>
                <option value="caregiving">Caregiving</option>
                <option value="end_of_life">End of Life</option>
                <option value="memorial">Memorial</option>
                <option value="gratitude">Gratitude</option>
            </select>
          </div>

          <div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.location_required}
                onChange={(e) => setFormData({ ...formData, location_required: e.target.checked })}
                className="mt-1"
              />
              <div>
                <div className="font-medium">Location Required</div>
                <div className="text-sm text-gray-500">This task requires being at a specific location</div>
              </div>
            </label>
          </div>

          {formData.location_required && (
            <div>
              <Label htmlFor="location_details">Location Details</Label>
              <Input
                id="location_details"
                value={formData.location_details}
                onChange={(e) => setFormData({ ...formData, location_details: e.target.value })}
                placeholder="e.g., CVS Pharmacy, 123 Main St"
                className="mt-1 border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-opacity-20"
              />
            </div>
          )}

          <div className="border-t pt-4">
            <Label className="mb-3 block font-semibold">Who Can See This Task?</Label>
            <div className="space-y-3 bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-600 mb-2">
                ✓ Inner Circle (always has access to all tasks)
              </p>
              
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.shared_with_support}
                  onChange={(e) => setFormData({ ...formData, shared_with_support: e.target.checked })}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">Share with Support Team</div>
                  <div className="text-sm text-gray-500">Friends and family helpers can see this task</div>
                </div>
              </label>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.shared_with_providers}
                  onChange={(e) => setFormData({ ...formData, shared_with_providers: e.target.checked })}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">Share with Providers</div>
                  <div className="text-sm text-gray-500">Healthcare professionals and service providers can see this task</div>
                </div>
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-blue-700 hover:bg-blue-800">
              {loading ? 'Creating...' : 'Create Task'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Tasks;
